import React from 'react';

const sans = "'DM Sans', system-ui, sans-serif";
const serif = "'DM Serif Display', Georgia, serif";

function TermsOfService({ onClose }) {
  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        <div style={styles.header}>
          <div>
            <span style={styles.logoAppt}>Appt</span>
            <span style={styles.logoNote}>Note</span>
          </div>
          <button style={styles.closeBtn} onClick={onClose}>✕</button>
        </div>

        <div style={styles.content}>
          <h1 style={styles.h1}>Terms of Service</h1>
          <p style={styles.meta}>Last updated: 9 October 2026</p>

          <p style={styles.p}>
            By using ApptNote (apptnote.com) you agree to these Terms of Service.
            Please read them carefully before using the service.
          </p>

          <h2 style={styles.h2}>1. What ApptNote is</h2>
          <p style={styles.p}>
            ApptNote is a tool that helps women track symptoms, identify patterns,
            and prepare summaries for medical appointments. It uses artificial
            intelligence to analyse symptom tracking data and generate insights
            based on the information you provide.
          </p>

          <h2 style={styles.h2}>2. What ApptNote is not</h2>
          <p style={styles.p}>
            ApptNote is <strong>not a medical service</strong>. It does not:
          </p>
          <ul style={styles.ul}>
            <li style={styles.li}>Provide medical advice, diagnosis or treatment</li>
            <li style={styles.li}>Replace consultation with a qualified healthcare professional</li>
            <li style={styles.li}>Guarantee the accuracy or completeness of any generated insights or reports</li>
            <li style={styles.li}>Constitute a medical device or diagnostic tool</li>
          </ul>
          <p style={styles.p}>
            All reports generated by ApptNote are based solely on information
            you provide. The accuracy of your report depends on the accuracy
            and consistency of your input.
          </p>

          <h2 style={styles.h2}>3. Medical disclaimer</h2>
          <p style={styles.p}>
            ApptNote insights and reports are intended solely to facilitate
            communication between you and your healthcare provider. They do
            not constitute medical advice and should not be used as a substitute
            for professional medical assessment. Always follow the advice of
            your doctor or qualified health professional.
          </p>
          <p style={styles.p}>
            If you are experiencing a medical emergency, call your local
            emergency number immediately (112 or 999 in Ireland and the UK,
            911 in the United States) or go to your nearest emergency department.
          </p>
          <p style={styles.p}>
            If at any time you experience severe, sudden, or concerning symptoms —
            including but not limited to severe chest pain, difficulty breathing,
            signs of stroke, thoughts of self-harm, or severe mental health
            distress — contact a healthcare professional immediately. If you are
            having thoughts of self-harm, you can contact Samaritans on 116 123
            (Ireland and the UK) or call or text 988 (United States), free and at
            any time. ApptNote is not a substitute for urgent medical care.
          </p>

          <h2 style={styles.h2}>4. Your responsibilities</h2>
          <ul style={styles.ul}>
            <li style={styles.li}>You must be 18 years or over to use ApptNote</li>
            <li style={styles.li}>You are responsible for the accuracy of information you enter</li>
            <li style={styles.li}>You must not use ApptNote for any unlawful purpose</li>
            <li style={styles.li}>You must not attempt to circumvent usage limits or access restrictions</li>
            <li style={styles.li}>You must not share your account access with others</li>
            <li style={styles.li}>You must not use ApptNote to provide medical advice to others</li>
          </ul>

          <h2 style={styles.h2}>5. Subscriptions and billing</h2>
          <p style={styles.p}>
            ApptNote Pro is available as an annual subscription. The standard
            price is <strong>€59.99 per year</strong> (Ireland &amp; UK) or <strong>$69.99 per year</strong> (US), or the equivalent shown in
            your local currency. The price you pay is the one shown at checkout,
            which may include an introductory or founding member rate. If you
            join on a founding member rate, that rate applies for as long as your
            subscription stays active. Subscriptions are billed annually in
            advance and renew automatically each year unless cancelled.
          </p>
          <p style={styles.p}>
            We will email you before your subscription renews. You may cancel at
            any time by emailing <a href="mailto:hello@apptnote.com" style={styles.link}>hello@apptnote.com</a>{' '}
            or through the billing link in your Stripe receipt, and we will
            confirm your cancellation by email. Cancellation takes effect at the
            end of your current billing period — you keep Pro access until that
            date, and your account then continues on the free tier.
          </p>
          <p style={styles.p}>
            Payments are processed securely by Stripe. ApptNote does not store
            your payment card details. Prices are inclusive of applicable VAT where
            required by law.
          </p>

          <h2 style={styles.h2}>6. Refunds</h2>
          <p style={styles.p}>
            We offer a <strong>14-day refund</strong> from the date of purchase if
            you are not satisfied. To request a refund, contact us at{' '}
            <a href="mailto:hello@apptnote.com" style={styles.link}>hello@apptnote.com</a>{' '}
            within 14 days of your purchase date. Refunds are not available after
            this period or for partial subscription periods.
          </p>
          <p style={styles.p}>
            If you are based in the EU or UK, you may also have statutory cancellation
            rights under consumer protection law. Nothing in these terms affects
            those rights.
          </p>

          <h2 style={styles.h2}>7. Free tier</h2>
          <p style={styles.p}>
            ApptNote offers a free tier with daily symptom logging, a pattern
            calendar, and basic wellbeing tracking. Pro features — including
            AI-generated doctor reports and advanced insights — require a paid
            subscription. We reserve the right to change what is included in the
            free tier with reasonable notice.
          </p>

          <h2 style={styles.h2}>8. Your data</h2>
          <p style={styles.p}>
            Your symptom tracking data is stored securely and used solely to
            provide the ApptNote service. You can request access, export, or
            deletion of your data at any time by emailing{' '}
            <a href="mailto:privacy@apptnote.com" style={styles.link}>privacy@apptnote.com</a>.
            See our Privacy Policy for full details on how we handle your data.
          </p>
          <p style={styles.p}>
            Cancelling your subscription does not delete your data — your
            account continues on the free tier with your history intact. If you
            close your account, all your personal data is permanently deleted
            within 30 days, as described in our Privacy Policy.
          </p>

          <h2 style={styles.h2}>9. Intellectual property</h2>
          <p style={styles.p}>
            The ApptNote name, logo, interface, and AI analysis methods are our
            intellectual property. The symptom data you enter and the personalised
            reports generated from it are yours — we claim no ownership over your
            personal health information or appointment summaries.
          </p>

          <h2 style={styles.h2}>10. Service availability</h2>
          <p style={styles.p}>
            ApptNote is provided on a best-effort basis. We do not guarantee
            uninterrupted service and may need to perform maintenance, make
            changes, or discontinue features with reasonable notice.
          </p>

          <h2 style={styles.h2}>11. Limitation of liability</h2>
          <p style={styles.p}>
            ApptNote is provided on an as-is basis. To the maximum extent
            permitted by law, we accept no liability for any harm arising from
            use of ApptNote, including any medical decisions made based on
            ApptNote insights or reports. Our total liability to you shall not
            exceed the amount you have paid us in the 12 months preceding any
            claim, or €59.99 if no payment has been made.
          </p>
          <p style={styles.p}>
            Nothing in these terms excludes liability for death, personal injury
            caused by negligence, or any other liability that cannot be excluded
            under Irish law.
          </p>

          <h2 style={styles.h2}>12. Termination</h2>
          <p style={styles.p}>
            You may stop using ApptNote at any time and request deletion of your
            data. We may suspend or terminate your access if you breach these
            terms, use the service fraudulently, or in other reasonable
            circumstances. If we terminate your access without cause, we will
            provide a pro-rata refund for any remaining paid period.
          </p>

          <h2 style={styles.h2}>13. Changes to these terms</h2>
          <p style={styles.p}>
            We may update these terms as the service develops. Significant changes
            will be communicated to registered users by email at least 14 days
            before they take effect. Continued use of ApptNote after updated terms
            take effect constitutes acceptance of the revised terms.
          </p>

          <h2 style={styles.h2}>14. Governing law</h2>
          <p style={styles.p}>
            These terms are governed by the laws of Ireland. Any disputes shall be
            subject to the jurisdiction of the Irish courts. If you are a consumer,
            this does not take away any mandatory protections given to you by the
            law of the country where you live.
          </p>

          <h2 style={styles.h2}>15. Contact us</h2>
          <p style={styles.p}>
            For queries about these terms, billing, or your account, contact us at{' '}
            <a href="mailto:hello@apptnote.com" style={styles.link}>hello@apptnote.com</a>.
            For data protection queries, contact{' '}
            <a href="mailto:privacy@apptnote.com" style={styles.link}>privacy@apptnote.com</a>.
          </p>
        </div>

        <button style={styles.closeButton} onClick={onClose}>
          Close
        </button>
      </div>
    </div>
  );
}

const styles = {
  overlay: {
    position: 'fixed',
    top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(28, 42, 58, 0.75)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 2000,
    padding: '24px',
  },
  modal: {
    backgroundColor: '#FAF7F2',
    borderRadius: '16px',
    maxWidth: '640px',
    width: '100%',
    maxHeight: '85vh',
    overflowY: 'auto',
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '24px 32px',
    borderBottom: '1px solid rgba(44,44,42,0.08)',
    position: 'sticky',
    top: 0,
    backgroundColor: '#FAF7F2',
    zIndex: 1,
  },
  logoAppt: { fontFamily: sans, fontSize: '20px', fontWeight: '700', color: '#2C2C2A' },
  logoNote: { fontFamily: sans, fontSize: '20px', fontWeight: '700', color: '#D4A5A5' },
  closeBtn: {
    background: 'none', border: 'none', fontSize: '18px',
    cursor: 'pointer', color: '#6c8396', padding: '4px 8px', fontFamily: sans,
  },
  content: { padding: '32px' },
  h1: { fontFamily: serif, fontSize: '32px', fontWeight: '400', color: '#1a1a18', margin: '0 0 8px', lineHeight: '1.2' },
  meta: { fontFamily: sans, fontSize: '13px', color: '#6c8396', margin: '0 0 24px', fontWeight: '300' },
  h2: { fontFamily: serif, fontSize: '18px', fontWeight: '400', color: '#1a1a18', margin: '32px 0 8px', lineHeight: '1.2' },
  p: { fontFamily: sans, fontSize: '14px', color: '#4a6070', lineHeight: '1.7', margin: '0 0 12px', fontWeight: '300' },
  ul: { margin: '0 0 12px', paddingLeft: '20px' },
  li: { fontFamily: sans, fontSize: '14px', color: '#4a6070', lineHeight: '1.7', marginBottom: '6px', fontWeight: '300' },
  link: { color: '#7A9E87', textDecoration: 'underline' },
  closeButton: {
    width: 'calc(100% - 64px)',
    margin: '0 32px 32px',
    backgroundColor: '#2C2C2A',
    color: 'white',
    border: 'none',
    borderRadius: '10px',
    padding: '14px',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
    fontFamily: sans,
  },
};

export default TermsOfService;
